import React, { useState, useMemo, useRef } from 'react';
import {
  countryList,
  countryRegions,
  CONTINENT_OPTION_PREFIX,
  WORLDWIDE_LOCATION_LABEL,
  WORLDWIDE_LOCATION_VALUE,
} from '../../../constants/countries';

interface CountryRegionSelectorProps {
  selectedCountries: string[];
  selectedRegions: string[];
  isAnywhereSelected: boolean;
  onCountriesChange: (countries: string[]) => void;
  onRegionsChange: (regions: string[]) => void;
  onAnywhereChange: (isSelected: boolean) => void;
}

export const CountryRegionSelector: React.FC<CountryRegionSelectorProps> = ({
  selectedCountries,
  selectedRegions,
  isAnywhereSelected,
  onCountriesChange,
  onRegionsChange,
  onAnywhereChange,
}) => {
  const [emptyNotice, setEmptyNotice] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);
  /** Country just chosen, so a same-moment Worldwide reset does not replace it. */
  const lastPickRef = useRef<{ value: string; at: number } | null>(null);

  // Get all countries from selected regions
  const countriesFromRegions = useMemo(() => {
    const countries: string[] = [];
    selectedRegions.forEach(region => {
      const regionCountries = countryRegions[region as keyof typeof countryRegions] || [];
      countries.push(...regionCountries);
    });
    return countries;
  }, [selectedRegions]);

  // Combine selected countries and countries from regions, remove duplicates
  const allSelectedCountries = useMemo(() => {
    const combined = [...selectedCountries, ...countriesFromRegions];
    return Array.from(new Set(combined));
  }, [selectedCountries, countriesFromRegions]);

  // Handle selection from dropdown
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value;
    if (!value) return;

    const now = Date.now();
    const lastPick = lastPickRef.current;
    // Closing the list can report Worldwide in the same moment as the country just chosen.
    if (
      lastPick &&
      now - lastPick.at < 100 &&
      value === WORLDWIDE_LOCATION_VALUE &&
      lastPick.value !== WORLDWIDE_LOCATION_VALUE
    ) {
      return;
    }
    lastPickRef.current = { value, at: now };

    // Reset after the browser records this choice. Clearing during the event drops the first pick.
    const select = e.currentTarget;
    window.setTimeout(() => {
      if (selectRef.current === select) select.value = '';
    }, 0);

    // Check if it's worldwide (stored as ANYWHERE)
    if (value === WORLDWIDE_LOCATION_VALUE) {
      // Clear all countries and regions, select ANYWHERE
      onCountriesChange([]);
      onRegionsChange([]);
      onAnywhereChange(true);
      setEmptyNotice(false);
      return;
    }

    // If ANYWHERE is selected and user is adding a country/region, deselect ANYWHERE
    if (isAnywhereSelected) {
      onAnywhereChange(false);
    }
    setEmptyNotice(false);

    // Check if it's a region (check if value matches a region key)
    const regionKeys = Object.keys(countryRegions);
    const isRegion = regionKeys.includes(value);

    if (isRegion) {
      // Add region to selectedRegions
      if (!selectedRegions.includes(value)) {
        onRegionsChange([...selectedRegions, value]);

        // Add all countries from this region to selectedCountries
        const regionCountries = countryRegions[value as keyof typeof countryRegions] || [];
        const newCountries = [...selectedCountries];
        regionCountries.forEach(country => {
          if (!newCountries.includes(country)) {
            newCountries.push(country);
          }
        });
        onCountriesChange(newCountries);
      }
    } else {
      // It's a country - add to selectedCountries
      if (!selectedCountries.includes(value)) {
        onCountriesChange([...selectedCountries.filter(country => country !== 'ANYWHERE'), value]);
      }
    }
  };

  // Remove a country
  const handleRemoveCountry = (countryToRemove: string) => {
    // A region chip would put this country straight back. Drop those regions and
    // keep the rest of their countries as individual picks.
    const regionsToDrop = selectedRegions.filter(region =>
      (countryRegions[region as keyof typeof countryRegions] || []).includes(countryToRemove)
    );
    const nextRegions = selectedRegions.filter(region => !regionsToDrop.includes(region));
    const nextCountries = new Set(
      selectedCountries.filter(country => country !== countryToRemove && country !== 'ANYWHERE')
    );
    regionsToDrop.forEach(region => {
      (countryRegions[region as keyof typeof countryRegions] || []).forEach(country => {
        if (country !== countryToRemove) nextCountries.add(country);
      });
    });

    if (regionsToDrop.length > 0) onRegionsChange(nextRegions);
    const countryListNext = Array.from(nextCountries);
    onCountriesChange(countryListNext);

    const remainingFromRegions = nextRegions.flatMap(
      region => countryRegions[region as keyof typeof countryRegions] || []
    );
    setEmptyNotice(countryListNext.length === 0 && remainingFromRegions.length === 0);
  };

  // Remove a region (removes all countries from that region)
  const handleRemoveRegion = (regionToRemove: string) => {
    // Remove region from selectedRegions
    const updatedRegions = selectedRegions.filter(r => r !== regionToRemove);
    onRegionsChange(updatedRegions);

    // Remove all countries from this region
    const regionCountries = countryRegions[regionToRemove as keyof typeof countryRegions] || [];
    const updatedCountries = selectedCountries.filter(c => !regionCountries.includes(c));
    onCountriesChange(updatedCountries);

    // If no countries or regions left, select ANYWHERE
    const remainingCountriesFromRegions = updatedRegions.reduce((acc, region) => {
      const regionCountries = countryRegions[region as keyof typeof countryRegions] || [];
      return [...acc, ...regionCountries];
    }, [] as string[]);
    const allCountries = [...updatedCountries, ...remainingCountriesFromRegions];
    setEmptyNotice(allCountries.length === 0 && updatedRegions.length === 0);
  };

  // Clear all
  const handleClearAll = () => {
    onCountriesChange([]);
    onRegionsChange([]);
    onAnywhereChange(false);
    setEmptyNotice(true);
  };

  /**
   * Dropdown rank (express signup / investor location):
   * 1. Worldwide
   * 2. Broader regions (kept at top; labels prefixed so typeahead skips them)
   * 3. All countries A–Z
   *
   * Native select jumps to the first option starting with the typed letter.
   * Continents stay visible at the top, but typing "U" goes to Uganda, not United States.
   */
  const selectOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; isRegion: boolean; key: string }> = [];

    options.push({
      value: WORLDWIDE_LOCATION_VALUE,
      label: WORLDWIDE_LOCATION_LABEL.toUpperCase(),
      isRegion: false,
      key: 'anywhere',
    });

    Object.keys(countryRegions)
      .map(regionKey => ({
        value: regionKey,
        label: `${CONTINENT_OPTION_PREFIX}${regionKey.replace(/_/g, ' ').toUpperCase()}`,
        isRegion: true as const,
        key: `region-${regionKey}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach(opt => options.push(opt));

    countryList.forEach(country => {
      options.push({
        value: country,
        label: country,
        isRegion: false,
        key: `country-${country}`,
      });
    });

    return options;
  }, []);

  return (
    <div>
      <label className="block text-sm sm:text-base font-medium text-white mb-2 text-left">
        Acceptable countries/regions of startups
      </label>

      {/* Select dropdown */}
      <select
        ref={selectRef}
        defaultValue=""
        onChange={handleSelectChange}
        className="w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 appearance-none bg-no-repeat bg-right bg-[length:16px_16px] bg-[url('data:image/svg+xml,%3csvg xmlns=\\'http://www.w3.org/2000/svg\\' fill=\\'none\\' viewBox=\\'0 0 20 20\\'%3e%3cpath stroke=\\'%236b7280\\' stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'1.5\\' d=\\'m6 8 4 4 4-4\\'/%3e%3c/svg%3e')] pr-10"
        style={{
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          backgroundSize: '16px 16px',
          paddingRight: '40px',
        }}
      >
        <option value="" className="text-gray-900">
          Select countries / regions
        </option>
        {selectOptions.map(option => (
          <option key={option.key} value={option.value} className="text-gray-900">
            {option.label}
          </option>
        ))}
      </select>

      {emptyNotice && (
        <p className="mt-2 text-sm text-red-500 text-left">Select at least 1 location</p>
      )}

      {/* Selected items display */}
      {(isAnywhereSelected || allSelectedCountries.length > 0 || selectedRegions.length > 0) && (
        <div className="mt-4 p-4 bg-white rounded-md border border-gray-300 min-h-[100px]">
          <div className="flex flex-wrap gap-2">
            {/* Display worldwide tag (black tag) */}
            {isAnywhereSelected && (
              <div className="flex items-center gap-1 px-3 py-1 bg-gray-800 border border-gray-600 rounded-md">
                <span className="text-sm text-white">{WORLDWIDE_LOCATION_LABEL}</span>
                {/* Only show remove button if there are countries or regions selected */}
                {(allSelectedCountries.length > 0 || selectedRegions.length > 0) && (
                  <button
                    type="button"
                    onClick={() => onAnywhereChange(false)}
                    className="flex items-center justify-center w-4 h-4 rounded bg-gray-600 hover:bg-gray-700 text-white text-xs"
                  >
                    ×
                  </button>
                )}
              </div>
            )}

            {/* Display countries (white tags) - show all selected countries */}
            {allSelectedCountries.map((country, index) => (
              <div
                key={`country-${index}-${country}`}
                className="flex items-center gap-1 px-3 py-1 bg-white border border-gray-300 rounded-md"
              >
                <span className="text-sm text-gray-900">{country}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCountry(country)}
                  className="flex items-center justify-center w-4 h-4 rounded bg-gray-600 hover:bg-gray-700 text-white text-xs"
                >
                  ×
                </button>
              </div>
            ))}

            {/* Display regions (black tags) */}
            {selectedRegions.map((region, index) => {
              const regionName = region.replace(/_/g, ' ').toUpperCase();
              return (
                <div
                  key={`region-${index}-${region}`}
                  className="flex items-center gap-1 px-3 py-1 bg-gray-800 border border-gray-600 rounded-md"
                >
                  <span className="text-sm text-white">{regionName}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveRegion(region)}
                    className="flex items-center justify-center w-4 h-4 rounded bg-gray-600 hover:bg-gray-700 text-white text-xs"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clear all button */}
      {(isAnywhereSelected || allSelectedCountries.length > 0 || selectedRegions.length > 0) && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={handleClearAll}
            className="text-white text-sm hover:text-emerald-400 transition-colors"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default CountryRegionSelector;
